import { Divider, Flex } from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import CustomFormPageTitleAndDescription from '@/common/CustomFormPageTitleAndDescription/CustomFormPageTitleAndDescription';
import './SummaryPage.scss';
import { createLabelMapFromFieldNames } from '@/utils/createLabelMapFromFieldNames';

interface SummaryPageProps {
  title: string;
  description: string;
  isStandaloneForm: boolean;
  steps: { title: string }[];
  activeStep: number;
  goBackToSelectedStep: (step: number) => void;
}

const SummaryPage = ({
  title,
  description,
  isStandaloneForm,
  steps,
  activeStep,
  goBackToSelectedStep,
}: SummaryPageProps) => {
  const { getValues } = useFormContext();
  const { formConfig, formDetails, contactDetails } = getValues();

  const formDetailsLabelMap = createLabelMapFromFieldNames(
    formConfig.formPageFieldNamesAndLabels
  );

  const contactDetailsLabelMap = createLabelMapFromFieldNames(
    formConfig.contactDetailsPageFieldNamesAndLabels
  );

  return (
    <Flex className={'summary-page-container'}>
      <CustomFormPageTitleAndDescription
        title={title}
        description={description}
        isStandaloneForm={isStandaloneForm}
        steps={steps}
        activeStep={activeStep}
        goBackToSelectedStep={goBackToSelectedStep}
      />

      <Divider />

      <h3 className={'section-title'}>Form Details</h3>

      <Flex className={'form-details-container'}>
        {Object.keys(formDetails).map(key => {
          const label = formDetailsLabelMap[key] ?? key;

          const formatValue = () => {
            if (Array.isArray(formDetails[key])) {
              // if all values in the array are false (no checkbox selected), return 'NIL'
              if (formDetails[key].every(item => !item)) {
                return 'NIL';
              }

              return formDetails[key].filter(item => item !== false).join(', ');
            }

            if (formDetails[key] === '') {
              return 'NIL';
            }

            // Single (non-group) checkboxes store a boolean value - an
            // unchecked checkbox should render as 'NIL' like other empty
            // optional fields, rather than the literal string 'false'.
            if (typeof formDetails[key] === 'boolean') {
              return formDetails[key] ? 'true' : 'NIL';
            }

            return formDetails[key];
          };

          const value = formatValue();

          return (
            <Flex
              className={'label-and-value-container form-details'}
              key={key}
            >
              <span className={'label'}>{label}</span>
              {typeof value === 'string' && value.includes(',') ? (
                <ul>
                  {value.split(',').map((element: string) => (
                    <li key={element} className={'value'}>
                      {element.trim()}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className={'value'}>{String(value)}</span>
              )}
            </Flex>
          );
        })}
      </Flex>

      <Divider />

      <h3 className={'section-title'}>Contact Details</h3>

      <Flex className={'personal-details-container'}>
        {Object.keys(contactDetails)
          .filter(
            key =>
              ![
                'saveContactDetails',
                'privacyPolicy',
                'marketingConsent',
              ].includes(key)
          )
          .sort((a, b) => {
            const order = [
              'email',
              'salutation',
              'firstName',
              'lastName',
              'personalWebsite',
              'companyName',
              'companyWebsite',
              'industry',
              'jobLevel',
              'jobFunction',
              'companyHqLocation',
              'placeOfResidence',
            ];
            return order.indexOf(a) - order.indexOf(b);
          })
          .map(key => {
            const label = contactDetailsLabelMap[key] ?? key;
            const formatValue = () => {
              if (contactDetails[key] === '') {
                return 'NIL';
              }

              if (key === 'personalWebsite' || key === 'companyWebsite') {
                return (
                  <a href={`${contactDetails[key]}`} target={'_blank'}>
                    {`${contactDetails[key]}`}
                  </a>
                );
              }

              return contactDetails[key];
            };

            const value = formatValue();

            return (
              <Flex className={'label-and-value-container'} key={key}>
                <span className={'label'}>{label}</span>
                <span className={'value'}>{value}</span>
              </Flex>
            );
          })}
      </Flex>
    </Flex>
  );
};

export default SummaryPage;
